import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus, X } from 'lucide-react';
import { apiPut } from '../../lib/api.js';

const BASE_FIELDS = [
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
];

const toRows = (obj) => Object.entries(obj && typeof obj === 'object' ? obj : {})
  .filter(([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v))
  .map(([k, v]) => ({ k, v: v == null ? '' : String(v) }));

/**
 * In-place editor for a lead's contact details and custom fields. Saves with
 * PUT /leads/:id; the caller refreshes its caches in `onSaved`.
 * @param {Object} props
 * @param {string} props.leadId
 * @param {object} props.lead - Current lead (null-safe).
 * @param {() => void} props.onClose
 * @param {() => void} props.onSaved
 * @returns {JSX.Element}
 */
export default function EditLeadModal({ leadId, lead, onClose, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(BASE_FIELDS.map(([k]) => [k, lead?.[k] || ''])));
  const [custom, setCustom] = useState(() => toRows(lead?.customFields));
  const [saving, setSaving] = useState(false);

  async function save(e) {
    e.preventDefault();
    if (!form.firstName.trim() && !form.lastName.trim()) { toast.error('Enter a name'); return; }
    setSaving(true);
    try {
      const customFields = { ...(lead?.customFields || {}) };
      // Rebuild only the scalar keys we edited; nested values stay untouched.
      toRows(lead?.customFields).forEach(({ k }) => { delete customFields[k]; });
      custom.filter((r) => r.k.trim()).forEach((r) => { customFields[r.k.trim()] = r.v; });
      await apiPut(`/leads/${leadId}`, { ...form, customFields });
      toast.success('Lead updated');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not update lead');
    } finally {
      setSaving(false);
    }
  }

  const input = 'w-full bg-civix-panel border border-civix-border rounded-lg px-3 py-2 text-sm text-white focus:outline-hidden focus:border-civix-purple';

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={save} className="panel w-full max-w-md max-h-[90vh] overflow-y-auto p-5 space-y-3 bg-civix-panelLight border border-civix-border">
        <h3 className="font-semibold text-white">Edit Lead Details</h3>
        <div className="grid grid-cols-2 gap-2">
          {BASE_FIELDS.map(([key, label]) => (
            <label key={key} className={`text-[10px] uppercase tracking-wider text-white/40 ${['email', 'phone'].includes(key) ? 'col-span-2' : ''}`}>
              {label}
              <input
                className={`${input} mt-1 normal-case tracking-normal`}
                value={form[key]}
                type={key === 'email' ? 'email' : 'text'}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </label>
          ))}
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Custom fields</div>
          {custom.map((row, i) => (
            <div key={i} className="flex gap-2">
              <input className={input} placeholder="Field" value={row.k} onChange={(e) => setCustom((c) => c.map((r, j) => (j === i ? { ...r, k: e.target.value } : r)))} />
              <input className={input} placeholder="Value" value={row.v} onChange={(e) => setCustom((c) => c.map((r, j) => (j === i ? { ...r, v: e.target.value } : r)))} />
              <button type="button" onClick={() => setCustom((c) => c.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-300" aria-label="Remove field">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setCustom((c) => [...c, { k: '', v: '' }])} className="text-xs text-civix-purpleSoft hover:text-white flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> New field
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </form>
    </div>
  );
}
